<template>
  <div id="app" class="article-page">
    <HeadNav selected-language="en" page="for-pc" :gotodownload="gotodownload" />
    <main class="article-main">
      <article class="article-content">
        <header class="article-hero">
          <p class="article-kicker">Device Guide</p>
          <h1>GB WhatsApp for PC: How to Run GB WhatsApp on Windows</h1>
          <p class="article-lead">
            If you want to use <strong>GB WhatsApp on PC</strong>, you need an Android emulator
            because GB WhatsApp does not have a native Windows version.
          </p>
          <p>The basic setup is simple:</p>
          <p class="article-flow">Windows PC → Android emulator → GB WhatsApp APK</p>
        </header>

        <p>
          You can then open GB WhatsApp inside the emulator just as you would on an Android
          phone.
        </p>

        <img
          class="article-image"
          src="@/assets/gb-whatsapp-for-pc.webp"
          alt="GB WhatsApp for PC running through an Android emulator on Windows">

        <h2>How to Install GB WhatsApp on PC</h2>
        <ol class="article-steps">
          <li>
            <h3>Install an Android Emulator</h3>
            <p>First, install an Android emulator that supports your Windows PC.</p>
            <p>Common choices include:</p>
            <ul class="article-list">
              <li>BlueStacks</li>
              <li>LDPlayer</li>
              <li>NoxPlayer</li>
              <li>MEmu</li>
            </ul>
            <p>
              The emulator creates an Android environment inside Windows, allowing you to install
              Android apps such as GB WhatsApp.
            </p>
            <p>
              For a smoother experience, 8 GB or more RAM and an SSD are recommended. Hardware
              virtualization can also improve emulator performance.
            </p>
          </li>

          <li>
            <h3>Open the Emulator</h3>
            <p>
              After installing the emulator, launch it and wait for the Android home screen to
              appear.
            </p>
            <p>
              You can think of it as an Android phone running inside a Windows window.
            </p>
            <p>
              You don't need to install anything directly into Windows at this stage.
            </p>
          </li>

          <li>
            <h3>Install the GB WhatsApp APK</h3>
            <p>
              Download the <a :href="site.downloadPath">GB WhatsApp APK</a> to your computer.
            </p>
            <p>
              Then install the APK inside the emulator. Depending on the emulator, you can usually
              use an <strong>Install APK</strong> button or simply drag the APK onto the emulator
              window.
            </p>
            <p>After installation:</p>
            <ol class="article-list article-list--ordered">
              <li>Open the emulator.</li>
              <li>Find GB WhatsApp in the Android app list.</li>
              <li>Launch the app.</li>
              <li>Continue with the account setup.</li>
            </ol>
            <p>
              The important point is that the APK is running
              <strong>inside Android</strong>, not directly on Windows.
            </p>
          </li>
        </ol>

        <h2>How to Set Up GB WhatsApp</h2>
        <p>
          When you open GB WhatsApp for the first time, you will normally be asked to enter a phone
          number and complete account verification.
        </p>
        <p>
          Enter the number you want to use and follow the verification instructions shown by your
          version of GB WhatsApp.
        </p>
        <p>
          The exact process can vary between builds. If you do not receive the verification code,
          avoid repeatedly requesting new codes because WhatsApp may make you wait before trying
          again.
        </p>
        <p>
          If a particular GB WhatsApp build cannot complete verification, changing the emulator
          does not necessarily fix the problem. The issue may be related to the GB WhatsApp version
          or WhatsApp's handling of modified clients.
        </p>

        <h2>Can You Run GB WhatsApp on Windows 10 or 11?</h2>
        <p>
          Yes. You can use the emulator method on supported Windows 10 and Windows 11 computers.
        </p>
        <p>
          However, the emulator is responsible for most of the compatibility. Before installing
          one, check its current Windows requirements.
        </p>
        <p>If the emulator runs slowly, try:</p>
        <ul class="article-list">
          <li>Enabling hardware virtualization</li>
          <li>Closing unnecessary Windows programs</li>
          <li>Increasing the emulator's available RAM</li>
          <li>Reducing the display resolution</li>
          <li>Updating your graphics driver</li>
          <li>Installing the emulator on an SSD</li>
        </ul>
        <p>
          Older computers may struggle because an Android emulator requires additional CPU and
          memory on top of Windows.
        </p>

        <h2>Why Is GB WhatsApp Slow on My PC?</h2>
        <p>
          Running GB WhatsApp on a computer involves an extra software layer:
        </p>
        <p class="article-flow">Windows &rarr; Android emulator &rarr; GB WhatsApp</p>
        <p>That is why it can feel heavier than a normal Windows application.</p>
        <p>
          If GB WhatsApp takes a long time to open or messages feel delayed, check the emulator
          first. If the Android environment itself is lagging, reinstalling GB WhatsApp is
          unlikely to make much difference.
        </p>
        <p>
          Running fewer programs at the same time and giving the emulator enough system resources
          usually helps.
        </p>

        <h2>Can You Install GB WhatsApp Directly on Windows?</h2>
        <p>No.</p>
        <p>
          GB WhatsApp is normally distributed as an <strong>APK</strong>, which is an Android
          application package. It is not a normal Windows program.
        </p>
        <p>
          You may find websites offering files named something like
          <strong>GBWhatsApp-PC.exe</strong>, but this is not the standard GB WhatsApp Android
          package.
        </p>
        <p>
          Be particularly careful with unknown .exe files claiming to be a PC version of GB
          WhatsApp.
        </p>
        <p>The normal setup is:</p>
        <p>
          <strong>GB WhatsApp APK &rarr; Android emulator &rarr; Windows</strong>
        </p>
        <p>not:</p>
        <p><strong>GB WhatsApp APK &rarr; Windows</strong></p>

        <h2>GB WhatsApp for PC vs WhatsApp Web</h2>
        <p>
          If you only want to use WhatsApp from your computer, you may not actually need GB
          WhatsApp.
        </p>
        <p>
          <strong>WhatsApp Web</strong> and the official <strong>WhatsApp for Windows</strong>
          application allow you to use your WhatsApp account from a computer without installing an
          Android emulator.
        </p>
        <p>
          The difference is that they use the official WhatsApp service rather than running the
          modified Android GB WhatsApp application.
        </p>
        <p>So the choice is fairly straightforward:</p>
        <div class="comparison-wrap">
          <table class="comparison-table">
            <thead>
              <tr>
                <th>What you want</th>
                <th>Option</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>GB WhatsApp's modified features</td>
                <td data-label="Option">Android emulator + GB WhatsApp APK</td>
              </tr>
              <tr>
                <td>Normal WhatsApp on Windows</td>
                <td data-label="Option">Official WhatsApp for Windows</td>
              </tr>
              <tr>
                <td>WhatsApp in your browser</td>
                <td data-label="Option">WhatsApp Web</td>
              </tr>
              <tr>
                <td>Native GB WhatsApp Windows app</td>
                <td data-label="Option">Not available</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          If you specifically want the features found in GB WhatsApp, the emulator method is the
          relevant option.
        </p>
        <p>
          If you simply want to send messages from your PC, the official Windows app is much
          easier.
        </p>

        <h2>Is GB WhatsApp Safe on PC?</h2>
        <p>
          Using an emulator does not make GB WhatsApp an official WhatsApp application.
        </p>
        <p>
          GB WhatsApp is a third-party modified client, so it has different security and account
          considerations from the official app. WhatsApp has warned users that unsupported versions
          can result in account restrictions or other problems.
        </p>
        <p>
          The source of the APK also matters. Avoid downloading random files from websites that
          claim to offer a "GB WhatsApp PC version," particularly Windows executable files.
        </p>

        <p>
          Before installing an APK, check the version, file information, and source rather than
          assuming that every download page provides the same build.
        </p>

        <h2>Should You Use Official WhatsApp on PC?</h2>
        <p>
          If your main goal is simply to use WhatsApp on a larger screen,
          <strong>official WhatsApp for Windows is the better fit.</strong>
        </p>
        <p>It does not require:</p>
        <ul class="article-list">
          <li>An Android emulator</li>
          <li>An APK</li>
          <li>Extra RAM and CPU resources</li>
          <li>A separate Android environment</li>
        </ul>
        <p>
          You can install the official Windows application and connect it to your WhatsApp account
          using WhatsApp's supported device-linking system.
        </p>
        <p>
          GB WhatsApp is mainly worth considering if you specifically want the additional
          customization or modified features offered by the Android version and understand that it
          is a third-party client.
        </p>

        <h2>Final Thoughts</h2>
        <p>
          There is no native <strong>GB WhatsApp for PC</strong> application.
        </p>
        <p>
          To run GB WhatsApp on Windows, install an Android emulator first and then install the GB
          WhatsApp APK inside it. The emulator provides the Android environment that Windows needs
          to run the app.
        </p>
        <p>
          If you just want WhatsApp on your computer, however,
          <strong>
            official WhatsApp for Windows or WhatsApp Web is the simpler and officially supported
            option.
          </strong>
        </p>

      </article>
    </main>
  </div>
</template>

<script>
import HeadNav from '@/components/HeadNav.vue'
import site from '@/config/site.json'

export default {
  name: 'ForPc',
  components: { HeadNav },
  data() {
    return { site }
  },
  methods: {
    gotodownload() {
      window.location.href = site.downloadPath
    },
  },
}
</script>

<style lang="scss" scoped>
.article-page {
  min-height: 100vh;
  background: #f7f8fb;
}

.article-main {
  padding: 46px 16px 72px;
}

.article-content {
  width: min(920px, 100%);
  margin: 0 auto;
  padding: 42px 48px 50px;
  background: #fff;
  border: 1px solid #e3e7ef;
  border-radius: 16px;
  box-shadow: 0 14px 40px rgba(31, 45, 61, 0.08);
  color: #27313f;

  h1 {
    margin: 0 0 18px;
    color: #16243a;
    font-size: clamp(30px, 5vw, 46px);
    line-height: 1.15;
  }

  h2 {
    margin: 38px 0 12px;
    color: #182b49;
    font-size: clamp(22px, 3vw, 28px);
    line-height: 1.3;
  }

  h3 {
    margin: 24px 0 9px;
    color: #23446f;
    font-size: 19px;
  }

  p {
    margin: 0 0 18px;
    font-size: 17px;
    line-height: 1.75;
  }

  a {
    color: #245daf;
  }
}

.article-kicker {
  margin: 0 0 10px !important;
  color: #e35b24;
  font-size: 13px !important;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.article-lead {
  color: #536173;
  font-size: 19px !important;
}

.article-flow {
  display: inline-block;
  padding: 10px 14px;
  border-radius: 8px;
  background: #f0f4fa;
  color: #244c7f;
  font-weight: 700;
}

.article-image {
  display: block;
  width: 100%;
  height: auto;
  margin: 26px auto 30px;
  border: 1px solid #e0e6ef;
  border-radius: 12px;
  background: #f4f7fb;
}

.article-steps {
  margin: 26px 0 30px;
  padding: 0;
  list-style: none !important;
  list-style-type: none !important;
  counter-reset: article-step;

  > li {
    position: relative;
    margin-bottom: 30px;
    padding-left: 38px;
    counter-increment: article-step;
  }

  > li:last-child {
    margin-bottom: 0;
  }

  > li::before {
    position: absolute;
    top: 0;
    left: 0;
    color: #4774e8;
    font-size: 20px;
    font-weight: 800;
    line-height: 1.35;
    content: counter(article-step) ".";
  }

  h3 {
    margin: 0 0 10px;
    line-height: 1.35;
  }
}

.article-list {
  margin: 0 0 20px 22px;
  padding: 0;

  li {
    margin-bottom: 9px;
    line-height: 1.65;
  }
}

.article-list--ordered {
  list-style: decimal;
  padding-left: 20px;

  li {
    padding-left: 0;
  }
}

.comparison-wrap {
  margin: 22px 0 28px;
  overflow-x: auto;
  border: 1px solid #dce4ef;
  border-radius: 10px;
}

.comparison-table {
  width: 100%;
  min-width: 620px;
  border-collapse: collapse;

  th,
  td {
    padding: 13px 15px;
    border-bottom: 1px solid #dfe6ef;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: #edf4ff;
    color: #18375f;
  }

  tr:last-child td {
    border-bottom: 0;
  }
}

@media (max-width: 640px) {
  .article-main {
    padding: 22px 12px 48px;
  }

  .article-content {
    padding: 28px 20px 34px;
    border-radius: 12px;
  }

  .article-image {
    margin: 22px auto 26px;
    border-radius: 9px;
  }

  .comparison-wrap {
    overflow: visible;
    border: 0;
    border-radius: 0;
  }

  .comparison-table {
    display: block;
    min-width: 0;

    thead {
      display: none;
    }

    tbody,
    tr,
    td {
      display: block;
      width: 100%;
    }

    tr {
      margin-bottom: 14px;
      overflow: hidden;
      border: 1px solid #dce4ef;
      border-radius: 10px;
    }

    td {
      padding: 11px 13px;
      border-bottom: 1px solid #dfe6ef;
    }

    td:first-child {
      padding: 12px 14px;
      background: #edf4ff;
      color: #18375f;
      font-weight: 700;
    }

    td:not(:first-child) {
      display: block;
    }

    td:not(:first-child)::before {
      content: attr(data-label);
      display: block;
      margin-bottom: 4px;
      color: #18375f;
      font-weight: 700;
    }

    tr:last-child td:last-child {
      border-bottom: 0;
    }
  }

  .article-download {
    align-items: stretch;
    flex-direction: column;
  }

  .primary-download {
    text-align: center;
  }
}
</style>
